import React, { useState, useEffect, useCallback } from 'react';
import { incomeAPI } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';

/** שמות החודשים בעברית */
const HEBREW_MONTHS = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

/**
 * Incomes - דף ניהול הכנסות
 * מציג רשימת הכנסות עם אפשרויות סינון, הוספה, עריכה ומחיקה
 */
const Incomes = () => {
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  /**
   * טעינת קטגוריות הכנסה מהשרת
   */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await incomeAPI.getCategories();
        setCategories(response.data.categories || []);
      } catch (err) {
        console.error('שגיאה בטעינת קטגוריות:', err);
      }
    };
    fetchCategories();
  }, []);

  /**
   * טעינת הכנסות מהשרת לפי חודש ושנה שנבחרו
   */
  const fetchIncomes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await incomeAPI.getAll({
        month: selectedMonth,
        year: selectedYear,
      });
      setIncomes(response.data.incomes || []);
    } catch (err) {
      console.error('שגיאה בטעינת הכנסות:', err);
      setIncomes([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  /** טעינה ראשונית ובכל שינוי חודש/שנה */
  useEffect(() => {
    fetchIncomes();
  }, [fetchIncomes]);

  /**
   * עיצוב מספר למטבע ישראלי
   * @param {number} value - הערך המספרי
   * @returns {string} מחרוזת מעוצבת עם סימן שקל
   */
  const formatCurrency = (value) => {
    if (value == null) return '₪0';
    return `₪${Number(value).toLocaleString('he-IL')}`;
  };

  /**
   * עיצוב תאריך לתצוגה בעברית
   * @param {string} dateStr - מחרוזת תאריך
   * @returns {string} תאריך מעוצב
   */
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('he-IL');
  };

  /**
   * חישוב סכום כולל של כל ההכנסות המוצגות
   * @returns {number} סכום כולל
   */
  const totalAmount = incomes.reduce(
    (sum, income) => sum + Number(income.amount || 0),
    0
  );

  /**
   * פתיחת חלון הוספת הכנסה חדשה
   */
  const openAddModal = () => {
    setEditingIncome(null);
    setFormData({
      amount: '',
      category: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    });
    setError('');
    setShowModal(true);
  };

  /**
   * פתיחת חלון עריכת הכנסה קיימת
   * @param {Object} income - אובייקט ההכנסה לעריכה
   */
  const openEditModal = (income) => {
    setEditingIncome(income);
    setFormData({
      amount: income.amount || '',
      category: income.category || '',
      description: income.description || '',
      date: income.date
        ? new Date(income.date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
    });
    setError('');
    setShowModal(true);
  };

  /**
   * סגירת חלון ההוספה/עריכה
   */
  const closeModal = () => {
    setShowModal(false);
    setEditingIncome(null);
    setError('');
  };

  /**
   * עדכון שדה בטופס
   * @param {Event} e - אירוע שינוי בשדה
   */
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * שמירת הכנסה - הוספה חדשה או עדכון קיימת
   * @param {Event} e - אירוע שליחת הטופס
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const payload = { ...formData, amount: Number(formData.amount) };
      if (editingIncome) {
        await incomeAPI.update(editingIncome._id || editingIncome.id, payload);
      } else {
        await incomeAPI.create(payload);
      }
      closeModal();
      fetchIncomes();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'שגיאה בשמירת ההכנסה'
      );
    }
  };

  /**
   * מחיקת הכנסה לאחר אישור מהמשתמש
   * @param {string} id - מזהה ההכנסה למחיקה
   */
  const handleDelete = async (id) => {
    if (!window.confirm('האם למחוק הכנסה זו?')) return;

    try {
      await incomeAPI.delete(id);
      fetchIncomes();
    } catch (err) {
      console.error('שגיאה במחיקת הכנסה:', err);
    }
  };

  /**
   * תצוגת טעינה - ספינר
   */
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>טוען נתונים...</p>
      </div>
    );
  }

  return (
    <div className="page">
      {/* כותרת הדף */}
      <div className="page-header">
        <h1>הכנסות</h1>
        <p className="page-subtitle">ניהול ההכנסות שלך</p>
      </div>

      {/* סרגל סינון - בחירת חודש, שנה וכפתור הוספה */}
      <div className="filter-bar">
        <select
          className="form-control"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
        >
          {HEBREW_MONTHS.map((month, index) => (
            <option key={index + 1} value={index + 1}>
              {month}
            </option>
          ))}
        </select>
        <select
          className="form-control"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {Array.from({ length: 11 }, (_, i) => 2020 + i).map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
        <button className="btn btn-success" onClick={openAddModal}>
          <FiPlus /> הוסף הכנסה
        </button>
      </div>

      {/* שורת סיכום - סה"כ הכנסות */}
      <div className="summary-line">
        <strong>סה"כ הכנסות: {formatCurrency(totalAmount)}</strong>
      </div>

      {/* טבלת הכנסות */}
      {incomes.length === 0 ? (
        <div className="empty-state">
          <p>אין הכנסות להצגה עבור התקופה שנבחרה</p>
        </div>
      ) : (
        <>
          {/* תצוגת שולחן - דסקטופ בלבד */}
          <div className="desktop-only">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>תאריך</th>
                    <th>קטגוריה</th>
                    <th>תיאור</th>
                    <th>סכום</th>
                    <th>פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {incomes.map((income) => (
                    <tr key={income._id || income.id}>
                      <td>{formatDate(income.date)}</td>
                      <td>
                        <span className="badge badge-info">
                          {income.category}
                        </span>
                      </td>
                      <td>{income.description}</td>
                      <td>{formatCurrency(income.amount)}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => openEditModal(income)}
                          title="עריכה"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            handleDelete(income._id || income.id)
                          }
                          title="מחיקה"
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* תצוגת כרטיסים - מובייל בלבד */}
          <div className="mobile-only">
            <div className="mobile-list">
              {incomes.map((income) => (
                <div className="mobile-card" key={income._id || income.id}>
                  <div className="mobile-card-row">
                    <span className="badge badge-info">{income.category}</span>
                    <span className="mobile-card-date">{formatDate(income.date)}</span>
                  </div>
                  {income.description && <p className="mobile-card-desc">{income.description}</p>}
                  <div className="mobile-card-row">
                    <span className="mobile-card-amount income">{formatCurrency(income.amount)}</span>
                    <div className="mobile-card-actions">
                      <button className="btn btn-sm btn-outline" onClick={() => openEditModal(income)} title="עריכה"><FiEdit2 /></button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(income._id || income.id)} title="מחיקה"><FiTrash2 /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* חלון מודאלי להוספה/עריכה */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            {/* כותרת המודאל */}
            <div className="modal-header">
              <h2>
                {editingIncome ? 'עריכת הכנסה' : 'הוספת הכנסה'}
              </h2>
              <button className="btn btn-sm btn-outline" onClick={closeModal}>
                <FiX />
              </button>
            </div>

            {/* גוף המודאל - טופס */}
            <div className="modal-body">
              {error && <div className="alert-error">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="amount">סכום</label>
                  <input
                    id="amount"
                    type="number"
                    name="amount"
                    className="form-control"
                    value={formData.amount}
                    onChange={handleFormChange}
                    placeholder="הזן סכום"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="category">קטגוריה</label>
                  <select
                    id="category"
                    name="category"
                    className="form-control"
                    value={formData.category}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">בחר קטגוריה</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="description">תיאור</label>
                  <input
                    id="description"
                    type="text"
                    name="description"
                    className="form-control"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="הזן תיאור"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="date">תאריך</label>
                  <input
                    id="date"
                    type="date"
                    name="date"
                    className="form-control"
                    value={formData.date}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                {/* כפתורי פעולה */}
                <div className="modal-footer">
                  <button type="submit" className="btn btn-primary">
                    {editingIncome ? 'עדכון' : 'שמירה'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={closeModal}
                  >
                    ביטול
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Incomes;
